import {
  getBackendLanguage,
  hasAnyTranslation,
  translateBackend,
} from '@assets/i18n/config';
import {
  ContextObjectTypes,
  OrganizationRuleGroupCombinator,
} from '@generated/graphql';
import EditIcon from '@mui/icons-material/Edit';
import type { RuleGroupDocument } from '@onevesthq/ov-conditions';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { Box } from '@ovComponents/1-primative/box/box';
import { Link } from '@ovComponents/1-primative/link/link';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { Switch } from '@ovComponents/2-component/switch/switch';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import { ConditionalRuleBuilder } from '@ovComponents/3-pattern/conditionalRuleBuilder/conditionalRuleBuilder';
import { useFeatureFlags } from '@providers/userContextProvider';
import { validateUrl } from '@utils/common';
import { FlagsmithFlags } from '@utils/flagsmith';
import { isNil } from 'lodash-es';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type ClientPageInterface,
  PageObjectType,
  type TabInterface,
} from '../pageConfigurationTypes';
import { formatUrl } from './utils';

export const EditTab = ({
  page,
  activeTab,
  setPage,
  hover,
  resetHoverState,
}: {
  page: ClientPageInterface;
  activeTab: number;
  setPage: (page: any) => void;
  hover: boolean;
  resetHoverState: () => void;
}) => {
  const { t } = useTranslation('pageConfiguration');
  const { flagEnabled } = useFeatureFlags();
  const [open, setOpen] = useState<boolean>(false);
  const [linkError, setLinkError] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [isRuleGroupValid, setIsRuleGroupValid] = useState<boolean>(true);
  const [tab, setTab] = useState<TabInterface>(page.tabs[activeTab]);
  const [enableLink, setEnableLink] = useState<boolean>(
    translateBackend(page.tabs[activeTab].translatedUrl) !== '',
  );

  useEffect(() => {
    if (hover && translateBackend(page.tabs[activeTab].translatedUrl) !== '') {
      setEnableLink(true);
    }
  }, [page, activeTab, hover, setEnableLink]);

  const isConditionalPageConfigurationFlagEnabled = flagEnabled(
    FlagsmithFlags.conditional_account_page_configuration,
  );
  const isConditionalPageConfigurationEnabled =
    isConditionalPageConfigurationFlagEnabled &&
    page.type === PageObjectType.ACCOUNT;

  const validateFields = () => {
    let allValid = true;
    const currentLang = getBackendLanguage();

    if (!tab.label[currentLang]) {
      allValid = false;
      setError(true);
    } else {
      setError(false);
    }

    if (enableLink) {
      const isUrlValid = validateUrl(translateBackend(tab.translatedUrl));
      if (!isUrlValid) {
        allValid = false;
        setLinkError(true);
      } else {
        setLinkError(false);
      }
    }

    return allValid;
  };

  const savePage = () => {
    const uPage = { ...page, tabs: [...page.tabs] };

    uPage.tabs[activeTab] = {
      ...page.tabs[activeTab],
      label: tab.label,
      icon: tab.icon?.trim() ?? '',
      condition: tab.condition,
      contextObject: tab.contextObject,
      translatedUrl: hasAnyTranslation(tab.translatedUrl)
        ? Object.fromEntries(
            Object.entries(tab.translatedUrl ?? {}).map(([key, value]) => [
              key,
              formatUrl(value),
            ]),
          )
        : undefined,
    };

    setPage(uPage);
    setError(false);
    setOpen(false);
    setLinkError(false);
    setEnableLink(false);
    resetHoverState();
  };

  const onSubmit = () => {
    if (validateFields()) savePage();
  };

  const onClose = (event?: any) => {
    event.stopPropagation();
    setOpen(false);
    setTab(page.tabs[activeTab]);
    setError(false);
    setLinkError(false);
    setEnableLink(false);
    resetHoverState();
  };

  const isValid: boolean = /^(?:[a-z]+(?:_[a-z]+)*)?$/.test(
    (tab.icon ?? '').trim(),
  );
  const disabled: boolean =
    !tab.label ||
    (!!tab.icon && !isValid) ||
    (isConditionalPageConfigurationEnabled &&
      !!tab.condition?.ruleGroup &&
      !isRuleGroupValid);

  const onClick = (event?: any) => {
    event.stopPropagation();
    event.preventDefault();
    setTab(page.tabs[activeTab]);
    setOpen(true);
  };

  const stopPropagation = (event?: any) => {
    event.stopPropagation();
  };

  return (
    <Box
      display='flex'
      justifyContent='center'
      alignItems='center'
      onClick={stopPropagation}
    >
      {hover && <EditIcon sx={{ fontSize: '14px', ml: 1 }} onClick={onClick} />}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={isConditionalPageConfigurationEnabled ? 'sm' : 'xs'}
        fullWidth
      >
        <DialogTitle>
          <Typography variant='headingMedium' sx={{ mr: '4px' }}>
            {t('editTab')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Form onSubmit={onSubmit}>
            <TranslatableTextField
              label={t('label')}
              fullWidth
              error={error}
              value={tab.label}
              onChange={(value) => {
                setTab({ ...tab, label: value });
                setError(!value[getBackendLanguage()]);
              }}
            />
            <TextField
              fullWidth
              label={t('icon')}
              value={tab.icon ?? ''}
              sx={{ mt: 3 }}
              error={!isValid}
              errorText={t('error.invalidIconErrorText')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTab({ ...tab, icon: e.target.value.trim() })
              }
              placeholder='account_balance'
            />
            <Typography variant='labelSmall'>
              <Link
                href='https://mui.com/material-ui/material-icons/'
                target='_blank'
              >
                {t('listOfIcons')}
              </Link>
            </Typography>
            <Switch
              label={t('addLinkTab.switch')}
              checked={enableLink}
              onChange={(onoff) => {
                setEnableLink(onoff);
                setLinkError(false);
              }}
              sx={{ mt: 3 }}
            />
            {enableLink && (
              <TranslatableTextField
                fullWidth
                sx={{ mt: 2 }}
                label={t('addLinkTab.label')}
                value={tab.translatedUrl || getLanguageDefaults()}
                onChange={(value) => setTab({ ...tab, translatedUrl: value })}
                error={linkError}
                errorText={t('addLinkTab.error')}
              />
            )}

            {isConditionalPageConfigurationEnabled && (
              <ConditionalRuleBuilder
                key={tab.label.en}
                ruleGroup={tab.condition?.ruleGroup as RuleGroupDocument}
                setCondition={(condition) =>
                  setTab((prev) => ({
                    ...prev,
                    condition,
                    contextObject:
                      prev.contextObject ?? ContextObjectTypes.User,
                  }))
                }
                onValidationChange={setIsRuleGroupValid}
                setContextObject={(contextObject: ContextObjectTypes) =>
                  setTab({
                    ...tab,
                    contextObject,
                    condition: {
                      ruleGroup: {
                        combinator: OrganizationRuleGroupCombinator.Or,
                        rules: [],
                      },
                    },
                  })
                }
                {...(!isNil(tab?.contextObject) && {
                  contextObject: tab?.contextObject,
                })}
              />
            )}

            <Box display='flex' flexDirection='row' justifyContent='end'>
              <Button
                type='submit'
                disabled={disabled}
                label={t('update')}
                sx={{ mt: 3 }}
              />
            </Box>
          </Form>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
